/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button,
  CircularProgress, Stack,
} from '@mui/material';
import Swal from 'sweetalert2';
import { formKeyHandler } from 'utils/formKeyboard';

const TEAL = '#00798E';

/**
 * A tiny keyboard-navigable dialog for creating a simple master record
 * (Category / Unit / Manufacturer / HSN) inline from any "Select / create"
 * field (Tally-style). Originally local to the GRN quick-add flow
 * (ItemQuickAdd); promoted here so other screens (e.g. Item Detail's own
 * HSN field) can reuse it too.
 *
 *   props:
 *     open, title, initialName
 *     fields   : [{ key, label, required, upper, type:'text'|'number', xs }]
 *     build    : (form) => payload      — shape the create payload
 *     create   : (payload) => Promise<res>
 *     onClose(), onCreated(record)
 */
export default function MiniMasterDialog({
  open, title, initialName = '', fields = [], build, create, onClose, onCreated,
}) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const init = {};
    fields.forEach((f, i) => {
      init[f.key] = i === 0 && initialName
        ? (f.upper ? initialName.toUpperCase() : initialName)
        : (f.default ?? '');
    });
    setForm(init);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (f) => (e) =>
    setForm((p) => ({ ...p, [f.key]: f.upper ? e.target.value.toUpperCase() : e.target.value }));

  const save = async () => {
    for (const f of fields) {
      if (f.required && String(form[f.key] ?? '').trim() === '') {
        Swal.fire({ icon: 'warning', title: `${f.label.replace(' *', '')} is required` });
        return;
      }
    }
    setSaving(true);
    try {
      const res = await create(build(form));
      onCreated(res?.data || res);
      onClose();
    } catch { /* handled */ }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ color: TEAL, fontWeight: 700, fontSize: '0.95rem', pb: 1 }}>{title}</DialogTitle>
      <DialogContent sx={{ pt: '14px !important' }} onKeyDown={formKeyHandler(save)}>
        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
          {fields.map((f, i) => (
            <TextField
              key={f.key}
              autoFocus={i === 0}
              fullWidth size="small"
              label={f.label}
              type={f.type === 'number' ? 'number' : 'text'}
              value={form[f.key] ?? ''}
              onChange={set(f)}
              helperText={f.helperText}
              inputProps={f.type === 'number' ? { min: 0, step: 0.01 } : undefined}
              sx={f.upper ? { '& .MuiInputBase-input': { textTransform: 'uppercase' } } : undefined}
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none', color: '#7b809a' }}>Cancel</Button>
        <Button onClick={save} variant="contained" disabled={saving}
          sx={{ textTransform: 'none', bgcolor: TEAL, color: 'white !important', fontWeight: 600, minWidth: 120, '&:hover': { bgcolor: '#016476' } }}>
          {saving ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : 'Save (Ctrl+↵)'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
